import { Injectable } from '@angular/core';
import { RestService, PagedResultDto } from '@abp/ng.core';
import { Observable, Subject } from 'rxjs';
import {
  GetAiTestingDefinitionsInput,
  AiTestingDefinitionSummaryDto,
  AiTestingDefinitionDto,
  AiTestingDefinitionCreateDto,
  AiTestingDefinitionUpdateDto,
  GetAiTestingRunsInput,
  AiTestingRunSummaryDto,
  AiTestingRunDto,
  AiTestingDefinitionNameDto,
  AiTestingDefinitionExportInputDto,
  AiTestingDefinitionImportInputDto,
} from './ai-testing.model';
import { SessionDetailDto } from 'src/app/insights/proxy/conversation.model';

const PATH = '/api/app/ai-testing-definitions';

@Injectable({
  providedIn: 'root',
})
export class AiTestingDefinitionService {
  apiName = 'app';
  refreshTestsList$ = new Subject<string>();
  refreshRunsList$ = new Subject<string>();

  constructor(private restService: RestService) {}

  getList(
    input: GetAiTestingDefinitionsInput,
  ): Observable<PagedResultDto<AiTestingDefinitionSummaryDto>> {
    return this.restService.request(
      {
        method: 'GET',
        url: PATH,
        params: {
          projectId: input.projectId,
          filterText: input.filterText,
          sorting: input.sorting,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
        },
      },
      { apiName: this.apiName },
    );
  }

  get(id: string): Observable<AiTestingDefinitionDto> {
    return this.restService.request(
      {
        method: 'GET',
        url: `${PATH}/${id}`,
      },
      { apiName: this.apiName },
    );
  }

  create(input: AiTestingDefinitionCreateDto): Observable<AiTestingDefinitionDto> {
    return this.restService.request(
      {
        method: 'POST',
        url: PATH,
        body: input,
      },
      { apiName: this.apiName },
    );
  }

  update(id: string, input: AiTestingDefinitionUpdateDto): Observable<AiTestingDefinitionDto> {
    return this.restService.request(
      {
        method: 'PUT',
        url: `${PATH}/${id}`,
        body: input,
      },
      { apiName: this.apiName },
    );
  }

  delete(id: string): Observable<void> {
    return this.restService.request(
      {
        method: 'DELETE',
        url: `${PATH}/${id}`,
      },
      { apiName: this.apiName },
    );
  }

  run(id: string): Observable<void> {
    return this.restService.request(
      {
        method: 'POST',
        url: `${PATH}/${id}/run`,
      },
      { apiName: this.apiName },
    );
  }

  getRunList(
    aiTestingDefinitionId: string,
    input: GetAiTestingRunsInput,
  ): Observable<PagedResultDto<AiTestingRunSummaryDto>> {
    return this.restService.request(
      {
        method: 'GET',
        url: `${PATH}/${aiTestingDefinitionId}/runs`,
        params: {
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
        },
      },
      { apiName: this.apiName },
    );
  }

  getRun(aiTestingDefinitionId: string, runId: string): Observable<AiTestingRunDto> {
    return this.restService.request(
      {
        method: 'GET',
        url: `${PATH}/${aiTestingDefinitionId}/runs/${runId}`,
      },
      { apiName: this.apiName },
    );
  }

  hasValidLlmConfiguration(): Observable<boolean> {
    return this.restService.request(
      {
        method: 'GET',
        url: `${PATH}/has-valid-llm-configuration`,
      },
      { apiName: this.apiName },
    );
  }

  cancelRun(aiTestingDefinitionId: string, runId: string): Observable<void> {
    return this.restService.request(
      {
        method: 'POST',
        url: `${PATH}/${aiTestingDefinitionId}/runs/${runId}/cancel`,
      },
      { apiName: this.apiName },
    );
  }

  deleteRun(aiTestingDefinitionId: string, runId: string): Observable<void> {
    return this.restService.request(
      {
        method: 'DELETE',
        url: `${PATH}/${aiTestingDefinitionId}/runs/${runId}`,
      },
      { apiName: this.apiName },
    );
  }

  getSessionDetail(
    aiTestingDefinitionId: string,
    runId: string,
    conversationSessionId: string,
  ): Observable<SessionDetailDto> {
    return this.restService.request(
      {
        method: 'GET',
        url: `${PATH}/${aiTestingDefinitionId}/runs/${runId}/sessions/${conversationSessionId}`,
      },
      { apiName: this.apiName },
    );
  }

  getNameList(projectId: string): Observable<AiTestingDefinitionNameDto[]> {
    return this.restService.request(
      {
        method: 'GET',
        url: `${PATH}/name-list/${projectId}`,
      },
      { apiName: this.apiName },
    );
  }

  export(input: AiTestingDefinitionExportInputDto): Observable<Blob> {
    return this.restService.request(
      {
        method: 'POST',
        url: `${PATH}/export`,
        body: input,
        responseType: 'blob',
      },
      { apiName: this.apiName },
    );
  }

  import(input: AiTestingDefinitionImportInputDto): Observable<void> {
    return this.restService.request(
      {
        method: 'POST',
        url: `${PATH}/import`,
        body: input,
      },
      { apiName: this.apiName },
    );
  }
}
